import React from 'react';
import { Button, ButtonProps } from '../Button';
import { cn } from '../../utils';

export interface LoadingButtonProps extends Omit<ButtonProps, 'children'> {
  /**
   * Whether the button is in a loading state
   */
  loading?: boolean;
  /**
   * Text to display when loading
   * @default "Loading..."
   */
  loadingText?: string;
  /**
   * Custom loading icon/spinner
   * @default DaisyUI loading spinner
   */
  loadingIcon?: React.ReactNode;
  /**
   * The button content when not loading
   */
  children: React.ReactNode;
}

/**
 * LoadingButton component that provides built-in loading states
 * Based on feedback from @orchard9ai/create-vite-vike-tauri generator team
 *
 * @example
 * ```tsx
 * <LoadingButton
 *   loading={isSubmitting}
 *   loadingText="Saving..."
 *   onClick={handleSubmit}
 * >
 *   Save Changes
 * </LoadingButton>
 * ```
 */
export const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
  (
    {
      loading = false,
      loadingText = 'Loading...',
      loadingIcon = <span className="loading loading-spinner loading-sm" />,
      children,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <Button
        ref={ref}
        disabled={disabled || loading}
        className={cn('focus-interactive', loading && 'opacity-80', className)}
        aria-busy={loading}
        {...props}
      >
        {loading ? (
          <span className="inline-flex items-center gap-2">
            {loadingIcon}
            <span>{loadingText}</span>
          </span>
        ) : (
          children
        )}
      </Button>
    );
  }
);

LoadingButton.displayName = 'LoadingButton';
